import type { PaginatedResponse } from "types/DataTypes.ts";

import { api } from "./api.ts";

export type SavedViewScope = "USER" | "ORGANIZATION";

export interface SavedView {
  id: number;
  name: string;
  url_path: string;
  scope: SavedViewScope;
  scope_verbose: string;
  created_by: number;
  created_by_name: string;
  description: string;
  created: string;
  modified: string;
  organization: number;
}

export interface CreateSavedViewRequest {
  name: string;
  url_path: string;
  scope: SavedViewScope;
  description?: string;
}

export interface UpdateSavedViewRequest {
  name?: string;
  url_path?: string;
  scope?: SavedViewScope;
  description?: string;
}

export const savedViewAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getSavedViews: build.query<
      PaginatedResponse<SavedView>,
      {
        scope?: SavedViewScope;
        created_by?: number;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ scope, created_by, limit = 25, offset = 0 }) => ({
        url: `api/v1/saved-views/`,
        method: "GET",
        params: {
          ...(scope ? { scope } : {}),
          ...(created_by ? { created_by } : {}),
          limit,
          offset,
        },
      }),
      providesTags: ["SavedViews"],
    }),

    getSavedViewById: build.query<SavedView, { id: number }>({
      query: ({ id }) => ({
        url: `api/v1/saved-views/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [
        { type: "SavedView", id },
        "SavedViews",
      ],
    }),

    createSavedView: build.mutation<
      SavedView,
      { data: CreateSavedViewRequest }
    >({
      query: ({ data }) => ({
        url: `api/v1/saved-views/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SavedViews"],
    }),

    updateSavedView: build.mutation<
      SavedView,
      {
        id: number;
        data: UpdateSavedViewRequest;
      }
    >({
      query: ({ id, data }) => ({
        url: `api/v1/saved-views/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SavedView", id },
        "SavedViews",
      ],
    }),

    deleteSavedView: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `api/v1/saved-views/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SavedView", id },
        "SavedViews",
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSavedViewsQuery,
  useGetSavedViewByIdQuery,
  useCreateSavedViewMutation,
  useUpdateSavedViewMutation,
  useDeleteSavedViewMutation,
} = savedViewAPI;
